import {PreviewFormat} from '../../types.ts';
import {useState} from 'react';
import FormConfirmEdit from './FormConfirmEdit.tsx';
import {toast} from 'react-toastify';
import humanFileSize from '../../lib/humanFileSize.ts';
import Icon from '../shared/Icon.tsx';
import {DownloadIcon, FileIcon, SquarePenIcon, Trash2Icon} from 'lucide-react';
import CopyButton from '../shared/CopyButton.tsx';
import ConfirmButton from '../shared/ConfirmButton.tsx';
import {useMutation} from 'react-query';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';
import getBlobInfoFromUri from '../../lib/getBlobInfoFromUri.ts';
import {isNotNullOrWhiteSpace} from '../../lib/isNullOrWhiteSpace.ts';
import AssetPreviewModal from './AssetPreviewModal.tsx';
import {
    DirectoryModel,
    FileDataModel,
    RenameFileResult
} from '../../api/types.ts';
import useRenameFile from '../../api/uploads/useRenameFile.ts';
import useDeleteFile from '../../api/uploads/useDeleteFile.ts';
import AssetPermissions from './AssetPermissions.ts';
import {placesText, renamedMessage} from './usageText.ts';

interface ContainerFileProps {
    directory: DirectoryModel;
    fileData: FileDataModel;
    permissions: AssetPermissions;
    refetch: () => void;
    // False while usage is still being worked out: a count of 0 may be wrong.
    referencesComplete: boolean;
    onShowUsages: (file: FileDataModel) => void;
}

const ContainerFile: React.FC<ContainerFileProps> = ({
    directory,
    fileData,
    permissions,
    refetch,
    referencesComplete,
    onShowUsages
}) => {
    const [isEditing, setIsEditing] = useState(false);

    const [showPreview, setShowPreview] = useState(false);

    const {containerName, blobName} = getBlobInfoFromUri(fileData.uri);

    const renameFile = useRenameFile();
    const renameFileMutation = useMutation(renameFile, {
        onSuccess: (result: RenameFileResult | null) => {
            const message = renamedMessage(
                'File',
                result?.referencesUpdated ?? 0,
                result?.referencesNotUpdated ?? 0
            );
            if (result?.referencesNotUpdated) {
                toast.warning(message);
            } else {
                toast.success(message);
            }
            refetch();
        },
        onError: (error: unknown) => {
            toast.error(unknownErrorToString(error));
        }
    });

    const onApplyFileRename = async (newValue: string) => {
        setIsEditing(false);

        if (!isNotNullOrWhiteSpace(newValue)) {
            toast.error('Invalid filename.');
            return;
        }

        if (
            directory.files.some(
                f => f.name.toLowerCase() == newValue.toLowerCase()
            )
        ) {
            toast.error('A file with that name already exists.');
            return;
        }

        const destinationFilePath =
            blobName.substring(0, blobName.lastIndexOf('/')) + '/' + newValue;

        await renameFileMutation.mutateAsync({
            containerName,
            sourceFilePath: blobName,
            destinationFilePath
        });
    };

    const deleteFile = useDeleteFile();
    const deleteFileMutation = useMutation(deleteFile, {
        onSuccess: () => {
            toast.success('File deleted');
            refetch();
        },
        onError: (error: unknown) => {
            toast.error(unknownErrorToString(error));
        }
    });

    const onDeleteFile = async () => {
        await deleteFileMutation.mutateAsync({containerName, blobName});
    };

    const references = fileData.references ?? null;

    const usage = () => {
        if (references === null || (references === 0 && !referencesComplete)) {
            return null;
        }

        return (
            <button
                type="button"
                className={
                    references > 0
                        ? 'btn btn-link btn-sm p-0 ms-2 file-usage'
                        : 'btn btn-link btn-sm p-0 ms-2 file-usage text-muted'
                }
                onClick={() => onShowUsages(fileData)}
                title="Where this file is used"
            >
                {references > 0 ? `Used in ${references}` : 'Not used'}
            </button>
        );
    };

    const deleteWarning =
        references !== null && references > 0
            ? `It's used in ${placesText(
                  references
              )} in saved content, which will show it as missing.`
            : referencesComplete
            ? "Nothing in saved content uses it, though a project's code could still put its name together as it runs."
            : 'Any project that uses it will show it as missing.';

    const getFileLink = () => {
        if (fileData.previewFormat != PreviewFormat.NoPreview) {
            return (
                <a
                    className="character-limit-100 d-flex w-60 overflow-hidden"
                    title={fileData.fileName}
                    href="#"
                    role="button"
                    onClick={event => (
                        event.preventDefault(), setShowPreview(true)
                    )}
                >
                    {fileData.fileName}
                </a>
            );
        }

        return (
            <a
                className="character-limit-100 d-flex w-60 overflow-hidden"
                title={fileData.fileName}
                href={fileData.uri}
                target="_blank"
                rel="noopener noreferrer"
            >
                {fileData.fileName}
            </a>
        );
    };

    return (
        <>
            {showPreview && (
                <AssetPreviewModal
                    asset={fileData}
                    onClose={() => setShowPreview(false)}
                />
            )}
            <div className="uploads-list-row position-relative overflow-hidden">
                <span className="uploads-list-row-name position-relative">
                    <div className="list-row-icon">
                        <Icon icon={FileIcon} />
                    </div>

                    {isEditing && (
                        <FormConfirmEdit
                            value={fileData.fileName}
                            onApply={onApplyFileRename}
                            onCancel={() => setIsEditing(false)}
                        />
                    )}
                    {!isEditing && (
                        <>
                            {getFileLink()}
                            <small className="fs-08 op-08 ps-2">
                                &nbsp;{humanFileSize(fileData.length)}
                            </small>
                            {usage()}
                        </>
                    )}
                </span>
                <span className="file-action-icons">
                    <CopyButton
                        cssClass="action-link"
                        copyText={fileData.uri}
                        title="Copy file URL"
                    />

                    <a
                        className="action-link"
                        href={fileData.uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Download ${fileData.fileName}`}
                    >
                        <Icon icon={DownloadIcon} />
                    </a>

                    {permissions.canRenameOrDeleteFile && (
                        <>
                            <button
                                className="action-link"
                                onClick={() => setIsEditing(true)}
                            >
                                <Icon
                                    icon={SquarePenIcon}
                                    title="Rename file"
                                />
                            </button>
                            <ConfirmButton
                                cssClass="action-link"
                                onClick={onDeleteFile}
                                message="Delete this file?"
                                body={deleteWarning}
                                confirmLabel="Delete file"
                                safeLabel="Keep file"
                            >
                                <Icon icon={Trash2Icon} title="Delete file" />
                            </ConfirmButton>
                        </>
                    )}
                </span>
            </div>
        </>
    );
};

export default ContainerFile;
